import {
  Box,
  Text,
  Badge,
  Group,
  Button,
  Stack,
  Alert,
  Center,
  Modal,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconInbox } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import dayjs from "dayjs";
import {
  useCorrectionRequests,
  useActOnCorrectionRequest,
} from "../../hooks/useCorrectionRequests";
import UpdatePatientInfoModal from "../modals/UpdatePatientInfoModal";
import type { CorrectionRequest } from "../../services/correctionRequestService";
import { correctionRefLabel } from "../../utils/formatters";

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim() || "(empty)";
}

const CORRECTION_STATUS_DISPLAY: Record<
  CorrectionRequest["status"],
  { color: string; label: string; borderColor: string; backgroundColor: string }
> = {
  pending: {
    color: "orange",
    label: "Pending",
    borderColor: "var(--mantine-color-orange-4)",
    backgroundColor: "var(--mantine-color-orange-0)",
  },
  applied: {
    color: "green",
    label: "Applied",
    borderColor: "var(--mantine-color-green-4)",
    backgroundColor: "var(--mantine-color-green-0)",
  },
  modified_and_applied: {
    color: "teal",
    label: "Modified & Applied",
    borderColor: "var(--mantine-color-teal-4)",
    backgroundColor: "var(--mantine-color-teal-0)",
  },
  discarded: {
    color: "gray",
    label: "Discarded",
    borderColor: "var(--mantine-color-gray-4)",
    backgroundColor: "var(--mantine-color-gray-0)",
  },
};

function getCorrectionStatusDisplay(status: CorrectionRequest["status"]) {
  return CORRECTION_STATUS_DISPLAY[status];
}

const FIELD_LABELS: Record<string, string> = {
  "patient.firstName": "First Name",
  "patient.lastName": "Last Name",
  "patient.dateOfBirth": "Date of Birth",
  "patient.sex": "Sex",
  "patient.medicalRecordNumber": "Medical Record Number",
  "contact.mobilePhone": "Mobile Phone",
  "contact.homePhone": "Home Phone",
  "contact.workPhone": "Work Phone",
  "contact.email": "Email",
  "address.street": "Street Address",
  "address.state": "State",
  "address.city": "City",
  "address.zipCode": "ZIP Code",
  "primaryInsurance.policyNumber": "Policy Number (Primary)",
  "primaryInsurance.authorizationNumber": "Authorization Number (Primary)",
  "primaryInsurance.groupNumber": "Group Number (Primary)",
  "primaryInsurance.groupName": "Group Name (Primary)",
  "primaryInsurance.patientRelationToInsured": "Relation to Insured (Primary)",
  "secondaryInsurance.policyNumber": "Policy Number (Secondary)",
  "secondaryInsurance.authorizationNumber": "Authorization Number (Secondary)",
  "secondaryInsurance.groupNumber": "Group Number (Secondary)",
  "secondaryInsurance.groupName": "Group Name (Secondary)",
  "secondaryInsurance.patientRelationToInsured":
    "Relation to Insured (Secondary)",
  "caseInfo.externalCaseNumber": "External Case Number",
  "caseInfo.dateReceived": "Date Received",
  "caseInfo.dateCollected": "Date Collected",
  "caseInfo.clinicalHistory": "Clinical History",
  "caseInfo.indications": "Indications",
  "caseInfo.impressions": "Impressions",
  "caseInfo.ruleOuts": "Rule Outs",
  "caseInfo.originatingPracticeId": "Originating Practice",
  "caseInfo.originatingPhysicianId": "Originating Physician",
  "caseInfo.originatingSurgeryCenterId": "Originating Surgery Center",
  "caseInfo.consultationReason": "Consultation Reason",
  "caseInfo.consultationOriginalDiagnosis": "Original Diagnosis",
  "caseInfo.consultationClinicalContext": "Clinical Context",
  "caseInfo.requestingPathologist": "Requesting Pathologist",
  "caseInfo.shippingPathologist": "Shipping Pathologist",
  "caseInfo.shippingStreet": "Shipping Street",
  "caseInfo.shippingState": "Shipping State",
  "caseInfo.shippingCity": "Shipping City",
  "caseInfo.shippingZipCode": "Shipping ZIP Code",
};

interface CorrectionsCenterProps {
  caseId: string;
  caseData?: any;
}

export function CorrectionsCenter({
  caseId,
  caseData,
}: CorrectionsCenterProps) {
  const { data } = useCorrectionRequests(caseId, undefined, {
    includeHistory: true,
  });
  const correctionRequests = data?.correctionRequests ?? [];

  if (correctionRequests.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="xs">
          <IconInbox size={32} color="var(--mantine-color-gray-5)" />
          <Text size="sm" c="dimmed">
            No correction history.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {correctionRequests.map((correction) => (
        <CorrectionCard
          key={correction.id}
          correction={correction}
          caseId={caseId}
          caseData={caseData}
        />
      ))}
    </Stack>
  );
}

interface CorrectionCardProps {
  correction: CorrectionRequest;
  caseId: string;
  caseData?: any;
}

function CorrectionCard({ correction, caseId, caseData }: CorrectionCardProps) {
  const actMutation = useActOnCorrectionRequest();
  const [reviewOpened, { open: openReview, close: closeReview }] =
    useDisclosure(false);
  const [
    discardConfirmOpened,
    { open: openDiscardConfirm, close: closeDiscardConfirm },
  ] = useDisclosure(false);

  const isLoading = actMutation.isPending;
  const isPending = correction.status === "pending";
  const isDirectCorrection = correction.source === "direct_correction";
  const statusDisplay = getCorrectionStatusDisplay(correction.status);
  const displayedChanges =
    correction.appliedChanges ?? correction.proposedChanges;

  const changedFields = Object.entries(displayedChanges ?? {}).filter(
    ([, diff]: [string, any]) =>
      String(diff?.before ?? "") !== String(diff?.after ?? "")
  );

  const handleDiscard = async () => {
    await actMutation.mutateAsync({
      requestId: correction.id,
      action: "discard",
      caseId,
    });
    closeDiscardConfirm();
  };

  return (
    <>
      <Box
        p="md"
        style={{
          border: `2px solid ${statusDisplay.borderColor}`,
          borderRadius: "var(--mantine-radius-md)",
          backgroundColor: statusDisplay.backgroundColor,
        }}
      >
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <IconAlertCircle size={20} color="var(--mantine-color-orange-6)" />
            {correctionRefLabel(correction.refNumber) && (
              <Badge size="sm" variant="outline" color="blue">
                {correctionRefLabel(correction.refNumber)}
              </Badge>
            )}
            <Text fw={700} size="sm">
              {isPending
                ? "Pending Correction Request"
                : isDirectCorrection
                ? "Direct Correction"
                : "Correction History"}
            </Text>
          </Group>
          <Group gap="xs">
            {correction.caseErrorReportId && (
              <Tooltip label="This correction is linked to an error report">
                <Badge color="red" variant="outline" size="sm">
                  {correction.caseErrorReportRefNumber != null
                    ? `Linked to Error E${correction.caseErrorReportRefNumber}`
                    : "Linked to Error"}
                </Badge>
              </Tooltip>
            )}
            <Badge color={statusDisplay.color}>{statusDisplay.label}</Badge>
          </Group>
        </Group>

        <Text size="xs" c="dimmed" mb="sm">
          {isDirectCorrection ? "Updated" : "Requested"} by{" "}
          {correction.requestor?.name} on{" "}
          {dayjs(correction.requestedAt).format("MMM D, YYYY")}
        </Text>

        {correction.requestorNote &&
          stripHtml(correction.requestorNote) !== "(empty)" && (
            <Alert color="blue" variant="light" mb="sm" p="xs">
              <Box
                className="widget-rich-text"
                dangerouslySetInnerHTML={{ __html: correction.requestorNote }}
              />
            </Alert>
          )}

        <Box mb="sm">
          <Text size="xs" fw={600} c="dimmed" mb={4}>
            {isPending ? "PROPOSED CHANGES" : "APPLIED CHANGES"}
          </Text>
          <Stack gap={4}>
            {changedFields.length === 0 ? (
              <Text size="xs" c="dimmed">
                No field-level change details retained.
              </Text>
            ) : (
              changedFields.map(([field, diff]: [string, any]) => (
                <Group key={field} gap="xs" wrap="nowrap" align="flex-start">
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ minWidth: 180, flexShrink: 0 }}
                  >
                    {FIELD_LABELS[field] ?? field}
                  </Text>
                  <Box
                    className="rich-text-display"
                    style={{
                      color: "var(--mantine-color-red-6)",
                      textDecoration: "line-through",
                      flex: 1,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: String(diff?.before ?? "") || "(empty)",
                    }}
                  />
                  <Text size="xs" style={{ flexShrink: 0 }}>
                    →
                  </Text>
                  <Box
                    className="rich-text-display"
                    style={{
                      color: "var(--mantine-color-green-7)",
                      fontWeight: 500,
                      flex: 1,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: String(diff?.after ?? "") || "(empty)",
                    }}
                  />
                </Group>
              ))
            )}
          </Stack>
        </Box>

        {isPending ? (
          <Group justify="space-between" mt="md">
            <Button
              color="red"
              variant="light"
              onClick={openDiscardConfirm}
              disabled={isLoading}
              aria-label="Discard this correction request"
            >
              Discard
            </Button>
            <Button
              color="blue"
              onClick={openReview}
              disabled={isLoading}
              aria-label="Review and apply this correction request"
            >
              Review
            </Button>
          </Group>
        ) : (
          <Text size="xs" c="dimmed" mt="md">
            {isDirectCorrection ? "Updated" : "Resolved"}
            {correction.resolvedBy?.name
              ? ` by ${correction.resolvedBy.name}`
              : ""}
            {correction.resolvedAt
              ? ` on ${dayjs(correction.resolvedAt).format("MMM D, YYYY")}`
              : ""}
          </Text>
        )}
      </Box>

      {isPending && (
        <>
          {/* Discard Confirmation */}
          <Modal
            opened={discardConfirmOpened}
            onClose={closeDiscardConfirm}
            title="Discard Correction Request"
            size="sm"
            centered
          >
            <Text size="sm" mb="lg">
              Are you sure you want to discard this correction request? This
              action cannot be undone.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={closeDiscardConfirm}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button color="red" onClick={handleDiscard} loading={isLoading}>
                Discard
              </Button>
            </Group>
          </Modal>

          {/* Review Modal — full form, pre-filled with proposed values */}
          <UpdatePatientInfoModal
            caseId={caseId}
            opened={reviewOpened}
            onClose={closeReview}
            isSignedOff
            caseData={caseData}
            editingCorrectionRequestId={correction.id}
            initialProposedChanges={correction.proposedChanges}
            reviewMode
            onDiscard={() => {
              closeReview();
              openDiscardConfirm();
            }}
          />
        </>
      )}
    </>
  );
}
